import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Users, ShieldCheck, Shield, UserX, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OfficersPage() {
  const { user } = useAuth();
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, officer: null });
  const [selectedRole, setSelectedRole] = useState('');

  const fetchOfficers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/officers`, { withCredentials: true });
      setOfficers(data);
    } catch (e) {
      toast.error('Failed to load officers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOfficers(); }, []);

  const handleRoleChange = async () => {
    if (!confirmDialog.officer || !selectedRole) return;
    try {
      await axios.put(
        `${API}/admin/officers/${confirmDialog.officer.email}/role`,
        { role: selectedRole },
        { withCredentials: true }
      );
      toast.success(`Role updated to ${selectedRole}`);
      setConfirmDialog({ open: false, type: null, officer: null });
      fetchOfficers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeactivate = async () => {
    if (!confirmDialog.officer) return;
    try {
      await axios.delete(
        `${API}/admin/officers/${confirmDialog.officer.email}`,
        { withCredentials: true }
      );
      toast.success('Officer deactivated');
      setConfirmDialog({ open: false, type: null, officer: null });
      fetchOfficers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const getRoleBadge = (role, status) => {
    if (status === 'pending') return <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100" variant="outline">Pending Approval</Badge>;
    if (status === 'rejected') return <Badge className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100" variant="outline">Rejected</Badge>;
    switch (role) {
      case 'admin':
        return <Badge className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100" variant="outline">Admin</Badge>;
      case 'officer':
        return <Badge className="bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100" variant="outline">Officer</Badge>;
      case 'deactivated':
        return <Badge className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100" variant="outline">Deactivated</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const handleApprove = async (email, approved) => {
    try {
      await axios.put(`${API}/admin/officers/${email}/approve`, { approved }, { withCredentials: true });
      toast.success(approved ? 'Officer approved' : 'Officer rejected');
      fetchOfficers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse text-slate-400">Loading officers...</div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-5xl mx-auto animate-fade-in" data-testid="officers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>
            Manage Officers
          </h1>
          <p className="text-slate-500 mt-1">Manage roles and permissions for municipal officers</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Users className="w-5 h-5" />
          <span className="font-semibold">{officers.length}</span> officers
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid="officers-table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider w-12">#</TableHead>
              <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Name</TableHead>
              <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Email</TableHead>
              <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Mobile</TableHead>
              <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Role</TableHead>
              <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Challans</TableHead>
              <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Collected</TableHead>
              <TableHead className="font-semibold text-slate-600 uppercase text-xs tracking-wider">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {officers.map((o, idx) => (
              <TableRow key={o.email} data-testid={`officer-row-${o.email}`} className="hover:bg-slate-50/50">
                <TableCell className="text-slate-400 font-medium text-sm">{idx + 1}</TableCell>
                <TableCell className="font-medium text-slate-900">{o.name}</TableCell>
                <TableCell className="text-slate-600 text-sm">{o.email}</TableCell>
                <TableCell className="text-slate-600 text-sm font-mono">{o.mobile || '-'}</TableCell>
                <TableCell>{getRoleBadge(o.role, o.status)}</TableCell>
                <TableCell className="font-semibold text-slate-900">{o.challan_count || 0}</TableCell>
                <TableCell className="text-slate-700">
                  {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(o.total_collected || 0)}
                </TableCell>
                <TableCell>
                  {o.status === 'pending' ? (
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`approve-${o.email}`}
                        onClick={() => handleApprove(o.email, true)}
                        className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 text-xs font-semibold"
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`reject-${o.email}`}
                        onClick={() => handleApprove(o.email, false)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 text-xs font-semibold"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" />
                        Reject
                      </Button>
                    </div>
                  ) : o.email !== user?.email && o.role !== 'deactivated' && o.status !== 'rejected' ? (
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`change-role-${o.email}`}
                        onClick={() => {
                          setSelectedRole(o.role === 'admin' ? 'officer' : 'admin');
                          setConfirmDialog({ open: true, type: 'role', officer: o });
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 text-xs font-semibold"
                      >
                        {o.role === 'admin' ? <Shield className="w-3.5 h-3.5 mr-1" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                        {o.role === 'admin' ? 'Demote' : 'Promote'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`deactivate-${o.email}`}
                        onClick={() => setConfirmDialog({ open: true, type: 'deactivate', officer: o })}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50 text-xs font-semibold"
                      >
                        <UserX className="w-3.5 h-3.5 mr-1" />
                        Deactivate
                      </Button>
                    </div>
                  ) : o.email === user?.email ? (
                    <span className="text-xs text-slate-400 italic">You</span>
                  ) : (
                    <span className="text-xs text-red-400 italic">Deactivated</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, officer: null })}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              {confirmDialog.type === 'role' ? 'Change Role' : 'Deactivate Officer'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.type === 'role'
                ? `Change ${confirmDialog.officer?.name}'s role to ${selectedRole}?`
                : `Deactivate ${confirmDialog.officer?.name}? They will no longer be able to log in.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, type: null, officer: null })} data-testid="cancel-dialog-btn">
              Cancel
            </Button>
            {confirmDialog.type === 'role' ? (
              <Button onClick={handleRoleChange} data-testid="confirm-role-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                Confirm
              </Button>
            ) : (
              <Button onClick={handleDeactivate} data-testid="confirm-deactivate-btn" className="bg-red-600 hover:bg-red-700 text-white">
                Deactivate
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
